import { useState } from 'react';
import { CmsSectionShell } from './CmsSectionShell.tsx';
import { SectionHeading } from '../SectionHeading.tsx';
import { themeOf, RICHTEXT, EYEBROW, type ThemeCardClasses } from './cmsTheme.ts';
import type { FaqSection, FaqSectionGroup, FaqSectionItem } from '../../lib/types.ts';

/**
 * Editorial FAQ accordion: question/answer items authored directly on the section (S3), plus any
 * items resolved server-side from referenced named FaqLists (CMS-063), rendered as expand/collapse
 * rows in the published order. Only one row is open at a time across the whole section. Pure: never
 * reads faqs.json / DataContext — that JSON remains published solely for the mobile app.
 *
 * A section can carry one flat list (`items`) or several named groups (`groups`, one per attached
 * FaqList). With more than one group, each group heading is shown above its cards; a single group
 * (or a flat list) shows no per-group heading.
 *
 * v2 layout: the section heading sits in its own narrow left column with the accordion beside it,
 * so a long list of questions reads as a column of rows rather than as a wall under a title.
 *
 * Answers render as sanitized HTML (mirrors CmsSection bodyHtml) rather than plain text: the
 * named FaqList answers (CMS-063) are authored with real formatting (<p>/<ul><li>/<a href>) for the
 * public PHP site accordion, and inline answers were never guaranteed plain-text either — a
 * single rendering path is simpler than branching per-item on origin.
 */
export function CmsFaqSection({ theme, guitar, heading, items, groups, columns = 1 }: FaqSection) {
  const t = themeOf(theme);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // Normalise to a list of groups: explicit groups win; otherwise a single unnamed group wrapping
  // the flat items. Drop empty groups so headings never sit above nothing.
  const resolvedGroups: readonly FaqSectionGroup[] = (groups && groups.length > 0)
    ? groups
    : [{ items: items ?? [] }];
  const nonEmpty = resolvedGroups.filter(g => g.items.length > 0);
  if (nonEmpty.length === 0) return null;

  // Per-group headings only make sense when there's more than one group.
  const showGroupHeadings = nonEmpty.length > 1;

  // Where each group's items start in one section-wide numbering — that shared index is what
  // keeps "one open at a time" working across groups. Derived up front rather than accumulated
  // inside the render below: a counter reassigned while building JSX is a write during render,
  // which the React Compiler rightly refuses.
  const groupOffsets: readonly number[] = nonEmpty.map((_, i) =>
    nonEmpty.slice(0, i).reduce((n, g) => n + g.items.length, 0),
  );

  const accordion = (
    <div className="space-y-8 md:space-y-10">
      {nonEmpty.map((group, gi) => {
        return (
          <FaqGroup
            key={gi}
            group={group}
            showHeading={showGroupHeadings}
            headingColor={t.heading}
            card={t.card}
            eyebrowColor={t.eyebrow}
            columns={columns}
            baseIndex={groupOffsets[gi] ?? 0}
            openIndex={openIndex}
            onToggle={i => setOpenIndex(openIndex === i ? null : i)}
          />
        );
      })}
    </div>
  );

  return (
    <CmsSectionShell theme={theme} guitar={guitar}>
      {heading ? (
        <div className="grid gap-8 md:gap-12 md:grid-cols-[1fr_2fr] items-start">
          <SectionHeading color={t.heading} eyebrowColor={t.eyebrow} className="md:mb-0 md:sticky md:top-24">
            {heading}
          </SectionHeading>
          {accordion}
        </div>
      ) : accordion}
    </CmsSectionShell>
  );
}

interface FaqGroupProps {
  readonly group: FaqSectionGroup;
  readonly showHeading: boolean;
  readonly headingColor: string;
  readonly card: ThemeCardClasses;
  readonly eyebrowColor: string;
  readonly columns: 1 | 2;
  readonly baseIndex: number;
  readonly openIndex: number | null;
  readonly onToggle: (index: number) => void;
}

function FaqGroup({ group, showHeading, headingColor, card, eyebrowColor, columns, baseIndex, openIndex, onToggle }: FaqGroupProps) {
  const groupItems = group.items;

  // Two columns on desktop (when asked, and there's more than one item to split): render two
  // separate panels side by side. Always a single column on mobile.
  const twoCol = columns === 2 && groupItems.length > 1;
  const splitAt = twoCol ? Math.ceil(groupItems.length / 2) : groupItems.length;
  const cols = twoCol ? [groupItems.slice(0, splitAt), groupItems.slice(splitAt)] : [groupItems];

  return (
    <div>
      {showHeading && group.heading && (
        <h3 className={`mb-4 font-display text-[22px] md:text-2xl leading-tight ${headingColor}`}>{group.heading}</h3>
      )}
      <div className={`grid gap-4 md:gap-5 items-start ${twoCol ? 'md:grid-cols-2' : ''}`}>
        {cols.map((col, ci) => {
          const colOffset = ci === 0 ? 0 : splitAt;
          return (
            <div key={ci} className={`rounded-card overflow-hidden ${card.bg} ${card.text}`}>
              {col.map((item, i) => {
                const index = baseIndex + colOffset + i;
                return (
                  <FaqRow
                    key={`${item.question}-${index}`}
                    item={item}
                    card={card}
                    eyebrowColor={eyebrowColor}
                    isFirst={i === 0}
                    open={openIndex === index}
                    onToggle={() => onToggle(index)}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface FaqRowProps {
  readonly item: FaqSectionItem;
  readonly card: ThemeCardClasses;
  readonly eyebrowColor: string;
  readonly isFirst: boolean;
  readonly open: boolean;
  readonly onToggle: () => void;
}

function FaqRow({ item, card, eyebrowColor, isFirst, open, onToggle }: FaqRowProps) {
  return (
    <div className={isFirst ? undefined : `border-t ${card.border}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full text-left flex items-center justify-between gap-6 px-5 md:px-7 py-4 md:py-5"
      >
        <span className="font-display text-lg md:text-xl leading-tight">{item.question}</span>
        <span className={`${EYEBROW} text-xl shrink-0 transition-transform ${eyebrowColor} ${open ? 'rotate-45' : ''}`} aria-hidden="true">+</span>
      </button>
      {open && (
        <div
          className={`px-5 md:px-7 pb-5 md:pb-6 -mt-1 ${RICHTEXT} ${card.muted}`}
          dangerouslySetInnerHTML={{ __html: item.answer }}
        />
      )}
    </div>
  );
}
